import type { ReactNode } from 'react';
import { Icon, type IconName } from '../icons/icons.js';
import styles from './dashboard.module.css';

/**
 * THE DASHBOARD COMPOSITION PATTERNS, from the approved Screen 00 reference.
 *
 * WHY THESE EXIST AS COMPONENTS. The reference does not draw cards with text in
 * them: it draws a specific anatomy, repeated. Every figure has a tinted icon
 * badge, a small uppercase label, a large value, a coloured delta with an
 * arrow, a period caption and a miniature chart. Every section header has a
 * badge, a title, a subtitle and a control on the right. Every list row has a
 * badge, a figure and a chevron. Built once here, that anatomy is the same on
 * every screen that uses it; built ad hoc per screen, it is eight slightly
 * different dashboards.
 *
 * EVERY VALUE IS A FROZEN TOKEN. No colour, size, radius or shadow below is
 * invented: the accents are the frozen chip tints, the type sizes are the
 * frozen KPI and chip sizes, and the geometry is `--x-card-padding`,
 * `--x-grid-gap` and `--radius-ui`. Where the reference and the frozen system
 * genuinely disagree — a multi-series chart palette, a sixth accent hue — the
 * conformance matrix records it rather than this file inventing a value.
 *
 * NOTHING HERE COMPUTES A FACT. A delta is rendered from two server figures, a
 * donut from server-supplied segments, a sparkline from server-supplied points.
 * A component that derived a number would be a second opinion on it.
 */

/**
 * The accent a badge, tile or row carries.
 *
 * SEMANTIC TONES MEAN SOMETHING. `success`, `warning`, `danger` and `info` are
 * the frozen four and are used where the hue is part of the message — a paid
 * invoice, an overdue item. `teal` and `neutral` mean nothing in particular.
 * `accent` is the v1.2 seventh status chip and is not a brand colour: it
 * carries `--color-text-head` rather than an ink of its own, so a seventh
 * status cannot become a seventh brand colour.
 */
export type AccentTone = 'teal' | 'success' | 'info' | 'warning' | 'danger' | 'neutral' | 'accent';

/**
 * The categorical chart palette (v1.2), for data visualisation only.
 *
 * FOR SEGMENTS WHOSE HUE CARRIES NO MEANING. A breakdown by status uses the
 * semantic tones, because green really does mean paid; a breakdown by
 * department, account or customer has no such mapping, and reaching for
 * `danger` there would invite a reader to see a problem that nobody reported.
 * Cycled in order, so the same series is the same colour every render.
 */
export type ChartSeries = 'series1' | 'series2' | 'series3' | 'series4';

export const CHART_SERIES: readonly ChartSeries[] = Object.freeze([
  'series1',
  'series2',
  'series3',
  'series4',
]);

/** A donut or funnel segment's colour: semantic where it means something. */
export type SegmentTone = AccentTone | ChartSeries;

const TONE_CLASS: Record<SegmentTone, string> = {
  teal: styles.toneTeal ?? '',
  success: styles.toneSuccess ?? '',
  info: styles.toneInfo ?? '',
  warning: styles.toneWarning ?? '',
  danger: styles.toneDanger ?? '',
  neutral: styles.toneNeutral ?? '',
  accent: styles.toneAccent ?? '',
  series1: styles.toneSeries1 ?? '',
  series2: styles.toneSeries2 ?? '',
  series3: styles.toneSeries3 ?? '',
  series4: styles.toneSeries4 ?? '',
};

export interface IconBadgeProps {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly size?: 'sm' | 'md' | 'lg';
}

/** The tinted rounded square the reference puts beside every figure and row. */
export function IconBadge({ icon, tone = 'teal', size = 'md' }: IconBadgeProps) {
  const dimension = size === 'sm' ? 13 : size === 'lg' ? 20 : 16;
  return (
    <span
      className={[styles.badge, styles[`badge_${size}`] ?? '', TONE_CLASS[tone]]
        .filter(Boolean)
        .join(' ')}
      aria-hidden="true"
    >
      <Icon name={icon} size={dimension} />
    </span>
  );
}

export interface SectionHeaderProps {
  readonly icon?: IconName;
  readonly tone?: AccentTone;
  readonly title: string;
  readonly subtitle?: string;
  /** The reference's right-hand control: "View all", a period selector. */
  readonly action?: ReactNode;
  /** A count beside the title, as Needs Attention carries. */
  readonly count?: number;
  readonly countTone?: AccentTone;
}

export function SectionHeader({
  icon,
  tone = 'teal',
  title,
  subtitle,
  action,
  count,
  countTone = 'danger',
}: SectionHeaderProps) {
  return (
    <div className={styles.sectionHeader}>
      {icon !== undefined && <IconBadge icon={icon} tone={tone} />}
      <span className={styles.sectionTitles}>
        <span className={styles.sectionTitle}>
          {title}
          {count !== undefined && (
            <span className={[styles.sectionCount, TONE_CLASS[countTone]].join(' ')}>
              {new Intl.NumberFormat('en-GB').format(count)}
            </span>
          )}
        </span>
        {subtitle !== undefined && <span className={styles.sectionSubtitle}>{subtitle}</span>}
      </span>
      {action !== undefined && <span className={styles.sectionAction}>{action}</span>}
    </div>
  );
}

export interface SparklinePoint {
  readonly value: number;
}

/**
 * The miniature bar chart beside each headline figure.
 *
 * BARS, NOT A LINE, because that is what the reference draws — and because a
 * line between two monthly points implies values in between that nobody
 * measured. Purely decorative to a screen reader: the figure and its delta
 * carry the meaning, and the bars repeat it without adding anything, so it is
 * `aria-hidden` rather than a chart nobody can read aloud.
 */
export function Sparkline({
  points,
  tone = 'teal',
}: {
  readonly points: readonly SparklinePoint[];
  readonly tone?: AccentTone;
}) {
  if (points.length === 0) return null;
  const highest = Math.max(...points.map((point) => point.value), 1);
  return (
    <span
      className={[styles.sparkline, TONE_CLASS[tone]].join(' ')}
      aria-hidden="true"
      data-testid="sparkline"
    >
      {points.map((point, index) => (
        <span
          key={index}
          className={styles.sparkBar}
          style={{ height: `${String(Math.max(8, (point.value / highest) * 100))}%` }}
        />
      ))}
    </span>
  );
}

export interface KpiCardProps {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly label: string;
  /** Already formatted by the caller. A card that formatted money would guess. */
  readonly value?: string | undefined;
  /** Percentage change on the previous period, from two server figures. */
  readonly changePercent?: number | undefined;
  readonly changeCaption?: string | undefined;
  readonly points?: readonly SparklinePoint[] | undefined;
  /** The server's own reason. A refused figure is never a zero. */
  readonly unavailable?: string | undefined;
  /** Replaces the sparkline — the reference's "Invoices by Status" card. */
  readonly chart?: ReactNode;
}

export function KpiCard({
  icon,
  tone = 'teal',
  label,
  value,
  changePercent,
  changeCaption,
  points,
  unavailable,
  chart,
}: KpiCardProps) {
  if (unavailable !== undefined) {
    return (
      <div className={styles.kpiCard}>
        <span className={styles.kpiTop}>
          <IconBadge icon={icon} tone="neutral" size="lg" />
        </span>
        <span className={styles.kpiBody}>
          <span className={styles.kpiLabel}>{label}</span>
          {/*
            THE FIXED HEADING THE PRODUCT ALREADY USES for exactly this, so a
            figure refused here and refused in Finance reads as one thing
            happening rather than two.
          */}
          <span className={styles.kpiRefusedTitle}>Not available for your access</span>
          <span className={styles.kpiRefusedReason}>{unavailable}</span>
        </span>
      </div>
    );
  }

  const rising = changePercent !== undefined && changePercent > 0;
  const falling = changePercent !== undefined && changePercent < 0;

  const miniature =
    chart ??
    (points !== undefined && points.length > 0 ? <Sparkline points={points} tone={tone} /> : null);

  return (
    <div className={styles.kpiCard}>
      <span className={styles.kpiTop}>
        <IconBadge icon={icon} tone={tone} size="lg" />
        {changePercent !== undefined && (
          <span
            className={[
              styles.kpiDelta,
              rising ? styles.kpiDeltaUp : '',
              falling ? styles.kpiDeltaDown : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span aria-hidden="true">{rising ? '↑' : falling ? '↓' : '■'}</span>
            {`${String(Math.abs(Math.round(changePercent)))}%`}
          </span>
        )}
      </span>
      <span className={styles.kpiBody}>
        <span className={styles.kpiLabel}>{label}</span>
        <span className={styles.kpiValue}>{value ?? '—'}</span>
      </span>
      {/*
       * A CHART GETS ITS OWN ROW; A SPARKLINE SHARES THE CAPTION'S.
       *
       * They were one slot, and a donut placed in a 20px-tall foot beside a
       * caption drew itself straight over its own legend — the ring and the
       * keys occupying the same pixels in a sixth-of-a-row card. A sparkline
       * is 20px of trend that belongs beside the period it is a trend over; a
       * donut is a chart with keys and needs the width of the card.
       */}
      {chart === undefined ? (
        (changeCaption !== undefined || miniature !== null) && (
          <span className={styles.kpiFoot}>
            <span className={styles.kpiCaption}>{changeCaption ?? ''}</span>
            {miniature}
          </span>
        )
      ) : (
        <>
          {changeCaption !== undefined && (
            <span className={styles.kpiFoot}>
              <span className={styles.kpiCaption}>{changeCaption}</span>
            </span>
          )}
          <span className={styles.kpiChart}>{chart}</span>
        </>
      )}
    </div>
  );
}

export interface DonutSegment {
  readonly label: string;
  readonly value: number;
  readonly tone: SegmentTone;
}

/**
 * The donut the reference draws inside a KPI card, with its total in the middle.
 *
 * SERVER-SUPPLIED SEGMENTS ONLY. The arithmetic here is turning values into arc
 * lengths, which is drawing; deciding what the segments are is the server's.
 * The accessible name carries the same breakdown the legend shows, so the chart
 * is not a picture somebody cannot read.
 */
export function DonutChart({
  segments,
  total,
  caption,
  label,
}: {
  readonly segments: readonly DonutSegment[];
  readonly total: string;
  readonly caption?: string;
  readonly label: string;
}) {
  const sum = segments.reduce((running, segment) => running + segment.value, 0);
  if (sum <= 0) return null;

  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  let consumed = 0;

  return (
    <span className={styles.donutWrap}>
      <svg
        className={styles.donut}
        viewBox="0 0 88 88"
        role="img"
        aria-label={`${label}: ${segments
          .map((segment) => `${String(segment.value)} ${segment.label}`)
          .join(', ')}`}
      >
        <circle className={styles.donutTrack} cx="44" cy="44" r={radius} />
        {segments.map((segment) => {
          const length = (segment.value / sum) * circumference;
          const offset = -consumed;
          consumed += length;
          return (
            <circle
              key={segment.label}
              className={[styles.donutArc, TONE_CLASS[segment.tone]].join(' ')}
              cx="44"
              cy="44"
              r={radius}
              strokeDasharray={`${String(length)} ${String(circumference - length)}`}
              strokeDashoffset={String(offset)}
            />
          );
        })}
      </svg>
      <span className={styles.donutCentre} aria-hidden="true">
        <span className={styles.donutTotal}>{total}</span>
        {caption !== undefined && <span className={styles.donutCaption}>{caption}</span>}
      </span>
    </span>
  );
}

/** The legend the reference puts beside the donut. */
export function DonutLegend({ segments }: { readonly segments: readonly DonutSegment[] }) {
  return (
    <ul className={styles.legend}>
      {segments.map((segment) => (
        <li key={segment.label} className={styles.legendRow}>
          <span
            className={[styles.legendDot, TONE_CLASS[segment.tone]].join(' ')}
            aria-hidden="true"
          />
          <span className={styles.legendValue}>
            {new Intl.NumberFormat('en-GB').format(segment.value)}
          </span>
          <span className={styles.legendLabel}>{segment.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** A "12 · Tasks assigned to me ›" row, as My Work draws them. */
export function CountRow({
  icon,
  tone = 'teal',
  count,
  label,
  onOpen,
}: {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly count: number;
  readonly label: string;
  readonly onOpen?: (() => void) | undefined;
}) {
  const body = (
    <>
      <IconBadge icon={icon} tone={tone} />
      <span className={styles.countValue}>{new Intl.NumberFormat('en-GB').format(count)}</span>
      <span className={styles.countLabel}>{label}</span>
      {onOpen !== undefined && (
        <span className={styles.rowChevron} aria-hidden="true">
          <Icon name="caret-right" size={13} />
        </span>
      )}
    </>
  );
  return (
    <li className={styles.rowItem}>
      {onOpen === undefined ? (
        <span className={styles.row}>{body}</span>
      ) : (
        <button type="button" className={[styles.row, styles.rowButton].join(' ')} onClick={onOpen}>
          {body}
        </button>
      )}
    </li>
  );
}

/** A row carrying a badge, text, a module chip and an age, as Needs Attention does. */
export function AttentionRow({
  icon,
  tone = 'warning',
  title,
  chip,
  age,
  ageTone = 'danger',
}: {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly title: string;
  readonly chip?: string;
  readonly age?: string;
  readonly ageTone?: AccentTone;
}) {
  return (
    <li className={styles.rowItem}>
      <span className={styles.row}>
        <IconBadge icon={icon} tone={tone} />
        <span className={styles.rowTitle}>{title}</span>
        {chip !== undefined && <span className={styles.moduleChip}>{chip}</span>}
        {age !== undefined && (
          <span className={[styles.rowAge, TONE_CLASS[ageTone]].join(' ')}>{age}</span>
        )}
      </span>
    </li>
  );
}

/** A row carrying an avatar, text, a module chip and a time, as Recent Activity does. */
export function ActivityRow({
  initials,
  title,
  chip,
  when,
}: {
  readonly initials: string;
  readonly title: string;
  readonly chip?: string;
  readonly when?: string;
}) {
  return (
    <li className={styles.rowItem}>
      <span className={styles.row}>
        <span className={styles.activityAvatar} aria-hidden="true">
          {initials}
        </span>
        <span className={styles.rowTitle}>{title}</span>
        {chip !== undefined && <span className={styles.moduleChip}>{chip}</span>}
        {when !== undefined && <span className={styles.rowAge}>{when}</span>}
      </span>
    </li>
  );
}

/** The tinted tile Quick Actions is a 2×3 grid of. */
export function ActionTile({
  icon,
  tone = 'teal',
  label,
  onSelect,
}: {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly label: string;
  readonly onSelect: () => void;
}) {
  return (
    <button type="button" className={styles.actionTile} onClick={onSelect}>
      <IconBadge icon={icon} tone={tone} size="lg" />
      <span className={styles.actionLabel}>{label}</span>
    </button>
  );
}

export function ActionTileGrid({ children }: { readonly children: ReactNode }) {
  return <div className={styles.actionGrid}>{children}</div>;
}

export function RowList({ children }: { readonly children: ReactNode }) {
  return <ul className={styles.rowList}>{children}</ul>;
}

/** The band a dashboard's sections sit in: four across, then two, then one. */
/**
 * **A BAND IS A FIXED-HEIGHT ROW, MEASURED FROM THE FROZEN REFERENCE.**
 *
 * `band` was a four-column grid whose row height came from whichever card had
 * the most in it, so the operational row stood 416 CSS pixels tall where the
 * reference draws 334, and the snapshot row 491 where the reference draws 244.
 * The result read as a sparse, scrolling page rather than a dashboard.
 *
 * The height is now the band's, not the content's, and it is the reference's
 * own: measured off `screen.png` at its 1.5x export scale, card top edge to
 * card bottom edge.
 */
export function DashboardBand({
  children,
  band = 'operational',
}: {
  readonly children: ReactNode;
  /** Which frozen row this is. Decides the row height, nothing else. */
  readonly band?: 'operational' | 'snapshot';
}) {
  return (
    <div className={[styles.band, band === 'snapshot' ? styles.band_snapshot : ''].join(' ')}>
      {children}
    </div>
  );
}

/** The row of headline figures, which the reference holds at six across. */
export function KpiRow({ children }: { readonly children: ReactNode }) {
  return <div className={styles.kpiRow}>{children}</div>;
}

/* ==========================================================================
   THE MODULE SNAPSHOT
   ========================================================================== */

export interface SnapshotStat {
  readonly label: string;
  /** Already formatted. A component that formatted money would be guessing. */
  readonly value?: string | undefined;
  readonly changePercent?: number | undefined;
  /** The server's own reason. A refused figure is never rendered as a zero. */
  readonly unavailable?: string | undefined;
}

export interface SnapshotCardProps {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly title: string;
  /** The reference's "This Month ⌄". A real control, or nothing. */
  readonly period?: ReactNode;
  readonly stats: readonly SnapshotStat[];
  /** A `BarChart`, a `DonutChart` with its legend, a `FunnelChart`, a list. */
  readonly visual?: ReactNode;
}

/**
 * A MODULE'S WHOLE POSITION, IN ONE CARD.
 *
 * The reference's bottom band is eight of these and each is a single bordered
 * card: header, then a 2/5 stat stack beside a 3/5 visual. Rendering a
 * module's figures as a row of separate KPI cards — which is what this screen
 * did — throws away the thing the composition is for, which is fitting a
 * module into a quarter of the workspace without it becoming a page.
 *
 * WITH NO VISUAL THE STATS TAKE THE CARD rather than leaving a hole beside
 * them. A module that has figures but nothing to plot is a real case, not a
 * broken one.
 */
export function SnapshotCard({
  icon,
  tone = 'teal',
  title,
  period,
  stats,
  visual,
}: SnapshotCardProps) {
  return (
    <section className={styles.snapshotCard}>
      <header className={styles.snapshotHeader}>
        <h3 className={styles.snapshotTitle}>
          <IconBadge icon={icon} tone={tone} size="sm" />
          <span className={styles.snapshotTitleText}>{title}</span>
        </h3>
        {period !== undefined && <span className={styles.snapshotPeriod}>{period}</span>}
      </header>
      <div
        className={[styles.snapshotBody, visual === undefined ? styles.snapshotBody_statsOnly : '']
          .filter(Boolean)
          .join(' ')}
      >
        {/*
         * NOT FOCUSABLE, BECAUSE IT NO LONGER SCROLLS. The stack used to be a
         * scroll region — which needs keyboard access, which is why it carried
         * a tabindex — and a snapshot card that hides part of itself behind a
         * scrollbar is not a snapshot. The figures divide the card's height
         * instead, and the scale is what gives way.
         */}
        <div className={styles.snapshotStats}>
          {stats.map((stat) => (
            <SnapshotStatistic key={stat.label} stat={stat} />
          ))}
        </div>
        {visual !== undefined && <div className={styles.snapshotVisual}>{visual}</div>}
      </div>
    </section>
  );
}

function SnapshotStatistic({ stat }: { readonly stat: SnapshotStat }) {
  if (stat.unavailable !== undefined) {
    return (
      <div className={styles.snapshotStat}>
        <span className={styles.snapshotStatLabel}>{stat.label}</span>
        {/* The same fixed heading the KPI card uses, so a refusal here and a
            refusal in Finance read as one thing happening rather than two. */}
        <span className={styles.snapshotRefused}>Not available for your access</span>
      </div>
    );
  }
  const rising = stat.changePercent !== undefined && stat.changePercent > 0;
  const falling = stat.changePercent !== undefined && stat.changePercent < 0;
  return (
    <div className={styles.snapshotStat}>
      <span className={styles.snapshotStatLabel}>{stat.label}</span>
      <span className={styles.snapshotStatValue}>{stat.value ?? '—'}</span>
      {stat.changePercent !== undefined && (
        <span
          className={[
            styles.snapshotStatDelta,
            rising ? styles.snapshotStatDeltaUp : '',
            falling ? styles.snapshotStatDeltaDown : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span aria-hidden="true">{rising ? '↑' : falling ? '↓' : '■'}</span>
          {`${String(Math.abs(Math.round(stat.changePercent)))}%`}
        </span>
      )}
    </div>
  );
}

export interface MiniBarSeries {
  readonly label: string;
  readonly tone: SegmentTone;
}

export interface MiniBarGroup {
  readonly label: string;
  /** One value per series, in the same order. */
  readonly values: readonly number[];
}

export interface MiniBarChartProps {
  readonly caption?: string;
  readonly series: readonly MiniBarSeries[];
  readonly groups: readonly MiniBarGroup[];
  /**
   * What the chart says, for a reader who cannot see it. Required, because the
   * alternative is a chart that is silent rather than one that is described.
   */
  readonly label: string;
}

/**
 * The grouped column chart the Finance snapshot draws.
 *
 * SEPARATE FROM THE FROZEN `BarChart`, DELIBERATELY. That one is the §15
 * full-width treatment: exactly two series, its own frozen teal and
 * compare-grey, legend below the plot. This is an 80px miniature inside a
 * quarter-width card, with N series drawn from the v1.2 categorical palette
 * and its legend above. Reusing the frozen component here would have meant
 * restyling a frozen component, which is a v2.0 change; adding one beside it
 * is not.
 *
 * IT PLOTS, IT DOES NOT CALCULATE. Heights are a proportion of the largest
 * value the caller passed, which is presentation; every number came from the
 * server. A chart that derived a total would be a second opinion on it.
 */
/**
 * **THE VISUAL SLOT WHEN THERE IS NOTHING TRUTHFUL TO PLOT.**
 *
 * A snapshot card is a stat stack beside a chart, and that proportion is the
 * composition — not a decoration that can be dropped when the data is thin.
 * Letting the stats take the whole card turned a balanced card into a tall
 * column of figures whenever a one-month window gave a month-grain metric its
 * single point, which is most of the time.
 *
 * So the slot keeps its share and SAYS WHY IT IS EMPTY. It is deliberately not
 * a chart: no axis, no bar of zero height, no flat line drawn through one
 * value. A flat line is a claim that nothing changed; the truth here is that
 * there is nothing yet to compare, and the reader is told how to ask for more.
 */
export function ChartUnavailable({
  title,
  detail,
}: {
  readonly title: string;
  readonly detail: string;
}) {
  return (
    <span className={styles.chartEmpty}>
      <span className={styles.chartEmptyTitle}>{title}</span>
      <span className={styles.chartEmptyDetail}>{detail}</span>
    </span>
  );
}

export function MiniBarChart({ caption, series, groups, label }: MiniBarChartProps) {
  const highest = Math.max(1, ...groups.flatMap((group) => [...group.values]));
  return (
    <figure className={styles.barFigure} aria-label={label} role="img">
      {caption !== undefined && <figcaption className={styles.chartCaption}>{caption}</figcaption>}
      <div className={styles.chartLegend} aria-hidden="true">
        {series.map((entry) => (
          <span
            key={entry.label}
            className={[styles.chartLegendItem, TONE_CLASS[entry.tone]].join(' ')}
          >
            <span className={styles.chartSwatch} />
            {entry.label}
          </span>
        ))}
      </div>
      <div className={styles.barPlot} aria-hidden="true">
        {groups.map((group) => (
          <span key={group.label} className={styles.barGroup}>
            {group.values.map((value, index) => (
              <span
                key={index}
                className={[styles.bar, TONE_CLASS[series[index]?.tone ?? 'neutral']].join(' ')}
                style={{ height: `${String(Math.max(4, (value / highest) * 100))}%` }}
              />
            ))}
          </span>
        ))}
      </div>
      <div className={styles.barAxis} aria-hidden="true">
        {groups.map((group) => (
          <span key={group.label} className={styles.barAxisLabel}>
            {group.label}
          </span>
        ))}
      </div>
    </figure>
  );
}

export interface FunnelStage {
  readonly label: string;
  readonly value: string;
  readonly tone: SegmentTone;
  /** 0–1. The caller decides the width, because the caller has the figures. */
  readonly share: number;
}

/**
 * The pipeline funnel the CRM snapshot draws.
 *
 * THE LABEL SITS ABOVE THE BAR, NOT INSIDE IT. The reference puts white text
 * on saturated fills; the v1.2 series are a desaturated ramp approved for
 * GRAPHICS at 3:1, and white on the lightest of them is 4.37:1 where text
 * needs 4.5. The contrast sweep and axe both caught a first attempt that put
 * the label inside the bar — nine tests over. Text on the card surface is the
 * only version of this that is readable whichever series a caller passes.
 *
 * NARROWING IS THE POINT, so each bar's width is its own share — but it never
 * falls below a third, because a stage nobody can read is a stage that may as
 * well not be drawn. Every stage states its own figure, so the shape is a
 * summary of numbers that are all present rather than a substitute for them.
 */
export function FunnelChart({
  stages,
  label,
}: {
  readonly stages: readonly FunnelStage[];
  readonly label: string;
}) {
  return (
    <div className={styles.funnel} aria-label={label} role="img">
      {stages.map((stage) => (
        <div key={stage.label} className={styles.funnelStage}>
          <span className={styles.funnelStageRow}>
            <span className={styles.funnelStageLabel}>{stage.label}</span>
            <span className={styles.funnelStageValue}>{stage.value}</span>
          </span>
          <span
            className={[styles.funnelStageBar, TONE_CLASS[stage.tone]].join(' ')}
            style={{
              width: `${String(Math.round(Math.min(1, Math.max(0.34, stage.share)) * 100))}%`,
            }}
            aria-hidden="true"
          />
        </div>
      ))}
    </div>
  );
}

/**
 * A donut beside its legend, which is how the reference lays the pair out.
 *
 * Exists so that every snapshot puts them the same way round. Stacked, the
 * ring ends up under a block of text and the card loses most of its height to
 * a list it could have read across.
 */
export function DonutWithLegend({
  segments,
  total,
  caption,
  label,
}: {
  readonly segments: readonly DonutSegment[];
  readonly total: string;
  /**
   * The word UNDER the total, inside the ring. Optional, because in a card
   * that already names the metric above the ring it is the name said twice —
   * and a long one wraps straight over the arc it is standing in.
   */
  readonly caption?: string | undefined;
  readonly label: string;
}) {
  return (
    <div className={styles.donutPair}>
      <DonutLegend segments={segments} />
      <DonutChart
        segments={segments}
        total={total}
        {...(caption === undefined ? {} : { caption })}
        label={label}
      />
    </div>
  );
}
